import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';

type ClearBoardDialogProps = {
  open: boolean;
  onClose: () => void;
  onClearBoard: (mode: 'votes' | 'all') => Promise<void>;
  fixedMode?: 'votes' | 'all';
};

const ClearBoardDialog = ({ open, onClose, onClearBoard, fixedMode }: ClearBoardDialogProps) => {
  const [clearMode, setClearMode] = useState<'votes' | 'all'>('votes');
  const [isClearingBoard, setIsClearingBoard] = useState(false);

  useEffect(() => {
    if (open) {
      setClearMode(fixedMode ?? 'votes');
    }
  }, [fixedMode, open]);

  const handleClose = () => {
    if (isClearingBoard) {
      return;
    }

    onClose();
  };

  const handleClearBoard = async () => {
    if (isClearingBoard) {
      return;
    }

    setIsClearingBoard(true);

    try {
      await onClearBoard(fixedMode ?? clearMode);
      onClose();
    } finally {
      setIsClearingBoard(false);
    }
  };
  const isVotesOnly = fixedMode === 'votes';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="clear-board-dialog-title"
    >
      <DialogTitle id="clear-board-dialog-title">
        {isVotesOnly ? 'Clear Votes' : 'Clear Board'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {isVotesOnly
              ? "Clear everyone's votes from this board. This will remove all votes, not just your own."
              : 'Are you sure you want to clear this board?'}
          </Typography>
          <Typography variant="body1">
            <strong>It cannot be undone</strong>.
          </Typography>
        </Box>
        {!fixedMode && (
          <RadioGroup
            value={clearMode}
            onChange={(event) => setClearMode(event.target.value as 'votes' | 'all')}
          >
            <FormControlLabel value="votes" control={<Radio />} label="Remove votes only" />
            <FormControlLabel
              value="all"
              control={<Radio />}
              label="Remove all participants, votes, comments and chat"
            />
          </RadioGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isClearingBoard}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => void handleClearBoard()}
          disabled={isClearingBoard}
        >
          {isVotesOnly ? 'Clear votes' : 'Clear Board'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClearBoardDialog;
